<template>
  <div>
    <v-row>
      <v-col>
        <h2 class="mb-3 mt-3 ml-3">{{ $t('routes.storage.storageUsage') }}</h2>
      </v-col>
    </v-row>

    <v-card elevation=0>
      <v-container fluid>
        <v-row>
          <v-col>
            <v-radio-group
              v-model="nodeType"
              hide-details
              inline
            >
              <v-radio
                v-for="nodeType in nodesType"
                :key="nodeType.value"
                :label="nodeType.label"
                :value="nodeType.value"
              />
            </v-radio-group>
          </v-col>
        </v-row>

        <v-row>
          <v-col>
            <v-data-table
              :headers="headers"
              :loading="nodesLoading[nodeType]"
              :items="nodes"
              :items-per-page="30"
              :items-per-page-options="[20, 30, 50, 100]"
              class="elevation-0"
            >
              <template v-slot:item.name="{ item }">
                <router-link :to="getItemUrl(item)">{{ item.name }}</router-link>
              </template>

              <template v-slot:item.path="{ item }">
                <span>
                  <span v-if="shouldShowEllipsis(item)">
                    <v-icon>mdi-dots-horizontal</v-icon>
                    <v-icon>mdi-chevron-right</v-icon>
                  </span>
                  <span v-if="item.parent?.parent">
                    <router-link :to="getItemUrl(item.parent.parent)">
                      {{ item.parent.parent.name }}
                    </router-link>
                    <v-icon>mdi-chevron-right</v-icon>
                  </span>

                  <span v-if="item.parent">
                    <router-link :to="getItemUrl(item.parent)">
                      {{ item.parent.name }}
                    </router-link>
                    <v-icon>mdi-chevron-right</v-icon>
                  </span>

                  <router-link :to="getItemUrl(item)">
                    {{ item.name }}
                  </router-link>
                </span>
              </template>

              <template v-slot:item.filesCount="{ item }">
                <span>{{ item.filesCount }}</span>
              </template>

              <template v-slot:item.filesSize="{ item }">
                <!-- TODO: The storage from the recycle bin does not have filesSize and filesCount properties
                Please fix in the backend -->
                <span>{{ formatSize((item.filesSize || 0), 2) }}</span>
              </template>
            </v-data-table>
          </v-col>
        </v-row>
      </v-container>
    </v-card>

  </div>
</template>


<script>
import { useAuthStore } from '@/stores/AuthStore.js'
import { useAccountStore } from '@/stores/AccountStore.js'
import { formatSize } from '@/components/formatters.js';
import { fromPairs } from 'lodash';

export default {
  name: 'StorageIndex',
  data() {
    const nodesType = [
      { label: this.$t('routes.storage.clients'), value: 'clients' },
      { label: this.$t('routes.storage.projects'), value: 'projects' },
      { label: this.$t('routes.storage.folders'), value: 'folders' },
      { label: this.$t('routes.storage.recyclebin'), value: 'recyclebin' },
    ];

    return {
      nodeType: 'clients',

      nodesType: nodesType,
      nodesByType: {},
      nodesLoaded: {},
      nodesLoading: {},

      headers: [
        { title: this.$t('routes.storage.name'), key: 'name' },
        { title: this.$t('routes.storage.path'), key: 'path', 'sortable': false },
        { title: this.$t('routes.storage.files'), key: 'filesCount' },
        { title: this.$t('routes.storage.size'), key: 'filesSize' },
      ],
    };
  },
  afterCreate() {
    if (!this.$canControlTenantConfig() && !this.$canBelongstoTeam()) {
      this.$router.push('/console/index');
    }
  },
  beforeMount() {
    this.reset();
  },
  mounted() {
    if (!this.$canControlTenantConfig()) {
      this.$router.push('/console/index');
    }

    this.refreshStorageUsage(this.nodeType);
  },
  watch: {
    activeAccesspoint() {
      this.reset();
      this.refreshStorageUsage(this.nodeType);
    },
    nodeType() {
      this.refreshStorageUsage(this.nodeType);
    },
  },
  methods: {
    formatSize,
    reset() {
      this.nodesByType = fromPairs(this.nodesType.map((v) => [ v.value, [] ]));
      this.nodesLoaded = fromPairs(this.nodesType.map((v) => [ v.value, false ]));
      this.nodesLoading = fromPairs(this.nodesType.map((v) => [ v.value, false ]));
    },
    async refreshStorageUsage(nodeType) {
      // skip the request if there is data
      if (this.nodesLoaded[nodeType] || this.nodesLoading[nodeType]) {
        return;
      }

      if (!this.activeAccesspoint) {
        return;
      }

      const payload = {
        nodeid: this.activeAccesspoint.node.id,
        nodeType: nodeType,
      };

      const accountStore = useAccountStore();
      try {
        this.nodesLoading[nodeType] = true;

        const data = await accountStore.getStorageUsage(payload);

        if (data) {
          this.nodesByType[nodeType] = data;
        }

        this.nodesLoaded[nodeType] = true;
      } catch (error) {
        if (error && error.response && error.response.data && error.response.data.error) {
          const localizedMessage = this.$localizeMessage(error.response.data.error, this.$t('backend.system-error'));
          this.$showMessage(localizedMessage, { type: 'error' });
        } else {
          console.log(error);
          this.$showMessage(this.$t('backend.system-error'), { type: 'error' });
        }
      } finally {
        this.nodesLoading[nodeType] = false;
      }
    },
  },
  computed: {
    activeAccesspoint() {
      const authStore = useAuthStore();
      return authStore.getActiveAccesspoint;
    },
    getAccesspointIds() {
      const authStore = useAuthStore();
      return authStore.getAccessPointIds;
    },
    shouldShowEllipsis() {
      return (node) => {
        if (node.parent && node.parent.parent) {
          if (!this.getAccesspointIds.includes(node.parent.parent.id)) {
            return true;
          }
        }
        return false;
      };
    },
    getItemUrl() {
      return (item) => {
        if (this.nodeType === 'recyclebin') {
          return `/recycle-bin/${this.activeAccesspoint.node.id}`;
        }

        return `/node/${item.id}`;
      };
    },
    nodes() {
      return this.nodesByType[this.nodeType];
    },
  },
}
</script>
